import { keyBy } from 'lodash';
import { sortBy } from 'lodash/fp';
import { DateTime } from 'luxon';

import InquiryStatusSticker from 'dashboard/components/InquiryStatusSticker';
import Pagination from 'dashboard/components/Pagination';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, { TableLinkTR, TableTD, TableTH } from 'dashboard/layouts/TableLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplateFilterOption } from 'dashboard/models/inquiry-template';
import { TemplateFilterOption } from 'dashboard/models/template';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { PaginationParams } from 'lib/interfaces/api';

interface AccountInquiriesTableProps {
  loading: boolean;
  inquiries: Inquiry[];
  inquiryTemplateFilterOptions?: (InquiryTemplateFilterOption | TemplateFilterOption)[];
  dataTest?: string;
  paginationParams: PaginationParams;
  pageSize: number;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
}

const AccountInquiriesTable = ({
  loading,
  inquiries = [],
  inquiryTemplateFilterOptions = [],
  dataTest,
  paginationParams,
  handlePageLinkClick,
  handlePageSizeClick,
  pageSize,
}: AccountInquiriesTableProps) => {
  const inquiryTemplateFilterOptionsById = keyBy(inquiryTemplateFilterOptions, 'id');
  const inquiryRows = sortBy([(o) => DateTime.fromISO(o.attributes['created-at'])], inquiries)
    .reverse()
    .map((inquiry, key) => (
      <InquiryTableRow
        key={key}
        inquiry={inquiry}
        inquiryTemplateFilterOption={
          inquiryTemplateFilterOptionsById[
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            inquiry.relationships?.template?.data?.id ??
              inquiry.relationships?.['inquiry-template']?.data?.id
          ]
        }
      />
    ));

  return (
    <Card data-test={dataTest}>
      <Card.Body>
        <Card.Title>Inquiries</Card.Title>

        <TableLayout
          dataTest='account-inquiries-table__table'
          isEmpty={inquiries.length === 0}
          isLoading={loading}
          showHeader={inquiries.length !== 0}
          headerTR={
            <>
              <TableTH>Template</TableTH>
              <TableTH>Inquiry ID</TableTH>
              <TableTH data-test='account-inquiries-table__created-at-header'>
                {`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
              </TableTH>
              <TableTH data-test='account-inquiries-table__status-header'>Status</TableTH>
            </>
          }
          nullState={
            <TableNullState dataTestBlock='account-inquiries'>
              <h3>Looks like there aren&#39;t any inquiries for this account yet.</h3>
            </TableNullState>
          }
        >
          {inquiryRows}
        </TableLayout>
        {inquiryRows.length > 0 && (
          <Pagination
            pageSize={pageSize}
            paginationParams={paginationParams}
            handlePageSizeClick={handlePageSizeClick}
            handlePageLinkClick={handlePageLinkClick}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default AccountInquiriesTable;

interface InquiryTableRowProps {
  inquiry: Inquiry;
  inquiryTemplateFilterOption: InquiryTemplateFilterOption | TemplateFilterOption;
}

const InquiryTableRow = ({ inquiry, inquiryTemplateFilterOption }: InquiryTableRowProps) => {
  const { status, 'created-at': createdAt } = inquiry.attributes;
  return (
    <TableLinkTR
      to={`/dashboard/inquiries/${inquiry.id}`}
      data-test={`account-inquiries-table-row__${inquiry.id}`}
      id='account-inquiries-table__row'
    >
      <TableTD data-test={`account-inquiries-table-row__${inquiry.id}--template-id`}>
        {inquiryTemplateFilterOption?.name}
      </TableTD>
      <TableTD>
        <MonoSpan>{inquiry.id}</MonoSpan>
      </TableTD>
      <TableTD>{DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
      <TableTD>
        <InquiryStatusSticker data-test={`inquiry-table-row__status--${status}`} status={status} />
      </TableTD>
    </TableLinkTR>
  );
};
